/**
 * ============================================================================
 * useMembershipMutations - TanStack Query mutations for membership operations
 * ============================================================================
 *
 * Provides add, update, delete, duplicate, and toggle mutations
 * with automatic cache invalidation and comprehensive error handling
 *
 * ============================================================================
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { vendorApi } from '../services/api';
import type { MembershipFormData } from '../components/memberships/MembershipFormModal';

interface MutationResult {
  success: boolean;
  message: string;
  data?: any;
}

/**
 * Extract user-friendly error message from API response
 */
function extractErrorMessage(error: unknown, defaultMessage: string): string {
  if (error instanceof AxiosError) {
    // Check for specific error response structure
    const response = error.response?.data;

    // Case 1: API returned a message
    if (response?.message) {
      return response.message;
    }

    // Case 2: API returned errors object (validation errors)
    if (response?.errors && typeof response.errors === 'object') {
      const firstError = Object.values(response.errors)[0];
      if (Array.isArray(firstError) && firstError.length > 0) {
        return firstError[0];
      }
    }

    // Case 3: HTTP status code messages
    if (error.response?.status === 400) {
      return response?.message || 'Invalid request. Please check your input.';
    }

    if (error.response?.status === 403) {
      return 'You do not have permission to perform this action.';
    }

    if (error.response?.status === 404) {
      return 'Package not found.';
    }

    if (error.response?.status === 409) {
      return response?.message || 'This operation conflicts with existing data.';
    }

    if (error.response?.status === 500) {
      return response?.message || 'Server error. Please try again later.';
    }
  }

  // Fallback to default message
  return defaultMessage;
}

/**
 * Hook providing membership mutations (add, update, delete, duplicate, toggle)
 */
export function useMembershipMutations(venueSlug: string | undefined) {
  const queryClient = useQueryClient();

  const addPackage = useMutation<MutationResult, Error, MembershipFormData>({
    mutationFn: async (formData: MembershipFormData) => {
      if (!venueSlug) throw new Error('No venue slug');

      try {
        const packageData = {
          package_name: formData.name,
          price: Number(formData.price),
          duration: Number(formData.duration),
          duration_type: formData.duration_type,
          description: formData.description,
          privileges_and_access: formData.facilities.join('\n'),
          is_active: formData.status === 'active',
        };

        await vendorApi.createMembershipPackage(venueSlug, packageData);

        return {
          success: true,
          message: 'Package created successfully!',
        };
      } catch (error) {
        const errorMessage = extractErrorMessage(
          error,
          'Failed to create package'
        );
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberships', venueSlug] });
    },
  });

  const updatePackage = useMutation<
    MutationResult,
    Error,
    { packageId: number; formData: MembershipFormData }
  >({
    mutationFn: async ({ packageId, formData }) => {
      if (!venueSlug) throw new Error('No venue slug');

      try {
        const packageData = {
          package_name: formData.name,
          price: Number(formData.price),
          duration: Number(formData.duration),
          duration_type: formData.duration_type,
          description: formData.description,
          privileges_and_access: formData.facilities.join('\n'),
          is_active: formData.status === 'active',
        };

        await vendorApi.updateMembershipPackage(venueSlug, packageId, packageData);

        return {
          success: true,
          message: 'Package updated successfully!',
        };
      } catch (error) {
        const errorMessage = extractErrorMessage(
          error,
          'Failed to update package'
        );
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberships', venueSlug] });
    },
  });

  const deletePackage = useMutation<MutationResult, Error, number>({
    mutationFn: async (packageId: number) => {
      if (!venueSlug) throw new Error('No venue slug');

      try {
        await vendorApi.deleteMembershipPackage(venueSlug, packageId);

        return {
          success: true,
          message: 'Package deleted successfully!',
        };
      } catch (error) {
        const errorMessage = extractErrorMessage(
          error,
          'Failed to delete package'
        );
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberships', venueSlug] });
    },
  });

  const duplicatePackage = useMutation<MutationResult, Error, number>({
    mutationFn: async (packageId: number) => {
      if (!venueSlug) throw new Error('No venue slug');

      try {
        await vendorApi.duplicateMembershipPackage(venueSlug, packageId);

        return {
          success: true,
          message: 'Package duplicated successfully!',
        };
      } catch (error) {
        const errorMessage = extractErrorMessage(
          error,
          'Failed to duplicate package'
        );
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberships', venueSlug] });
    },
  });

  const toggleStatus = useMutation<MutationResult, Error, number>({
    mutationFn: async (packageId: number) => {
      if (!venueSlug) throw new Error('No venue slug');

      try {
        await vendorApi.togglePackageActive(venueSlug, packageId);

        return {
          success: true,
          message: 'Package status updated!',
        };
      } catch (error) {
        const errorMessage = extractErrorMessage(
          error,
          'Failed to update package status'
        );
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberships', venueSlug] });
    },
  });

  return {
    addPackage,
    updatePackage,
    deletePackage,
    duplicatePackage,
    toggleStatus,
  };
}
